import {
  fetchExternalPrompt,
  StorybookApiError,
  submitExternalResponse,
  type StorybookPromptPayload,
} from "./api";
import { CONSENT_TEXT_VERSION, MIN_RESPONSE_CHARS } from "./config";
import {
  logPromptLoaded,
  logSubmitFailure,
  logSubmitStart,
  logSubmitSuccess,
} from "./diagnostics";

export type PageView =
  | { kind: "loading" }
  | {
      kind: "unavailable";
      title: string;
      message: string;
      tone?: "neutral" | "warm";
      retry?: "load" | "submit";
    }
  | { kind: "form"; prompt: StorybookPromptPayload; consentVersion: string }
  | { kind: "submitting" }
  | { kind: "success"; message: string }
  | { kind: "already_answered" };

const LENGTH_HINT = "Write a sentence or two, at least a few words.";
const LENGTH_VALIDATION_MESSAGE = "Please write a sentence or two in your own words.";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatCategory(category: string | null | undefined): string | null {
  if (!category?.trim()) return null;
  const c = category.trim();
  if (c.toLowerCase() === "import") return null;
  return c.replace(/_/g, " ");
}

function recipientHeading(prompt: StorybookPromptPayload): string | null {
  const label = prompt.recipient_label?.trim() || prompt.beloved_display_name?.trim();
  return label || null;
}

function unavailableForError(code: StorybookApiError["code"]): {
  title: string;
  message: string;
  tone: "neutral" | "warm";
} {
  switch (code) {
    case "token_expired":
      return {
        title: "This link has expired",
        message:
          "Storybook invitations stay open for a limited time. Please ask the person who invited you to send a fresh link when you're ready.",
        tone: "warm",
      };
    case "token_revoked":
      return {
        title: "This invitation is closed",
        message: "The person who invited you may have closed this link. They can send a new one anytime.",
        tone: "warm",
      };
    case "token_completed":
    case "response_exists":
      return {
        title: "Already answered",
        message: "This prompt has already been answered. Thank you for caring enough to share a story.",
        tone: "neutral",
      };
    case "token_invalid":
    case "invalid_token":
      return {
        title: "We couldn't find this link",
        message: "Please check the message you received and try opening the link again.",
        tone: "neutral",
      };
    case "missing_config":
      return {
        title: "This page isn't ready yet",
        message: "Please try again in a little while.",
        tone: "neutral",
      };
    case "network":
      return {
        title: "Connection trouble",
        message: "We couldn't reach the server. Check your connection and try again.",
        tone: "neutral",
      };
    case "rate_limit":
      return {
        title: "Please wait a moment",
        message: "Too many attempts in a short time. Try again in a little while.",
        tone: "neutral",
      };
    default:
      return {
        title: "This invitation isn't available",
        message: "We couldn't open this Storybook prompt right now. Please try again later.",
        tone: "neutral",
      };
  }
}

function brandHeader(): string {
  return `
    <header class="brand" aria-label="Locket">
      <div class="brand-mark" aria-hidden="true">✦</div>
      <p class="brand-name">Locket</p>
      <p class="brand-tag">Storybook</p>
    </header>
  `;
}

function shell(inner: string): string {
  return `
    <div class="page">
      <div class="page-texture" aria-hidden="true"></div>
      <main class="card" role="main">
        ${brandHeader()}
        ${inner}
      </main>
      <footer class="footer">
        <p>No app install required · Your words stay private in their locket</p>
      </footer>
    </div>
  `;
}

function stateIcon(kind: "success" | "neutral" | "warm", glyph: string): string {
  return `<div class="state-icon state-icon--${kind}" aria-hidden="true">${glyph}</div>`;
}

function renderLoading(): string {
  return shell(`
    <div class="state state--centered" aria-busy="true" aria-live="polite">
      <div class="spinner" aria-hidden="true"></div>
      <p class="state-title">Opening your invitation…</p>
      <p class="state-body">Just a moment.</p>
    </div>
  `);
}

function renderSubmitting(): string {
  return shell(`
    <div class="state state--centered" aria-busy="true" aria-live="polite">
      <div class="spinner" aria-hidden="true"></div>
      <p class="state-title">Sending your answer…</p>
      <p class="state-body">Please keep this page open for a moment.</p>
    </div>
  `);
}

function renderUnavailable(
  title: string,
  message: string,
  tone: "neutral" | "warm" = "neutral",
  retry?: "load" | "submit"
): string {
  const icon = tone === "warm" ? stateIcon("warm", "◌") : stateIcon("neutral", "—");
  const retryButton = retry
    ? `<button type="button" class="btn-secondary" id="retry-btn">Try again</button>`
    : "";
  return shell(`
    <div class="state state--centered">
      ${icon}
      <p class="state-eyebrow">Storybook</p>
      <h1 class="state-title">${escapeHtml(title)}</h1>
      <p class="state-body">${escapeHtml(message)}</p>
      ${retryButton}
    </div>
  `);
}

function renderSuccess(message: string): string {
  return shell(`
    <div class="state state--centered">
      ${stateIcon("success", "♡")}
      <p class="state-eyebrow">Thank you</p>
      <h1 class="state-title">${escapeHtml(message)}</h1>
      <p class="state-body">Your story has been safely received. You may close this page.</p>
      <p class="state-body">It will appear in their private Storybook.</p>
    </div>
  `);
}

function renderAlreadyAnswered(): string {
  return shell(`
    <div class="state state--centered">
      ${stateIcon("success", "✓")}
      <p class="state-eyebrow">Storybook</p>
      <h1 class="state-title">Already answered</h1>
      <p class="state-body">Thank you — your story was already received.</p>
      <p class="state-body">You may close this page.</p>
    </div>
  `);
}

function renderForm(prompt: StorybookPromptPayload, consentVersion: string): string {
  const category = formatCategory(prompt.prompt_category);
  const recipient = recipientHeading(prompt);
  const ownerHint = prompt.owner_first_name?.trim()
    ? `<p class="owner-hint">From ${escapeHtml(prompt.owner_first_name.trim())}</p>`
    : "";

  return shell(`
    <div class="invite">
      <h1 class="form-title">Share your answer</h1>
      <p class="invite-lead">You've been invited to share a story.</p>
      ${ownerHint}
      ${recipient ? `<p class="recipient-label">A question for <span>${escapeHtml(recipient)}</span></p>` : ""}
      ${category ? `<p class="prompt-category">${escapeHtml(category)}</p>` : ""}
      <figure class="prompt-block">
        <figcaption class="prompt-label">Their question</figcaption>
        <blockquote class="prompt-text">${escapeHtml(prompt.prompt_text.trim())}</blockquote>
      </figure>
    </div>

    <hr class="divider" aria-hidden="true" />

    <div class="privacy">
      <p>Your answer will be saved privately to their locket Storybook.</p>
      <p>Please share only in your own words.</p>
    </div>

    <form id="storybook-form" class="form" novalidate>
      <label class="field-label" for="response">Your answer</label>
      <textarea
        id="response"
        name="response"
        class="field-textarea"
        rows="8"
        required
        minlength="${MIN_RESPONSE_CHARS}"
        placeholder="Take your time. There is no wrong way to begin…"
        autocomplete="off"
        spellcheck="true"
      ></textarea>
      <p id="response-hint" class="field-hint">${LENGTH_HINT}</p>
      <p id="form-error" class="form-error" role="alert" hidden></p>

      <p class="voice-note" aria-hidden="true">Voice answers coming soon — written answers are welcome today.</p>

      <label class="consent">
        <input type="checkbox" id="consent" name="consent" required />
        <span>I understand my answer will be saved privately to their Storybook.</span>
      </label>
      <input type="hidden" name="consent_version" value="${escapeHtml(consentVersion)}" />

      <button type="submit" class="btn-primary" id="submit-btn">
        Send my answer
      </button>
    </form>
  `);
}

export function render(view: PageView): string {
  switch (view.kind) {
    case "loading":
      return renderLoading();
    case "unavailable":
      return renderUnavailable(view.title, view.message, view.tone, view.retry);
    case "form":
      return renderForm(view.prompt, view.consentVersion);
    case "submitting":
      return renderSubmitting();
    case "success":
      return renderSuccess(view.message);
    case "already_answered":
      return renderAlreadyAnswered();
  }
}

export async function loadPrompt(token: string): Promise<PageView> {
  try {
    const prompt = await fetchExternalPrompt(token);
    logPromptLoaded();
    const consentVersion = prompt.consent_text_version?.trim() || CONSENT_TEXT_VERSION;
    return { kind: "form", prompt, consentVersion };
  } catch (e) {
    if (e instanceof StorybookApiError) {
      if (e.code === "token_completed" || e.code === "response_exists") {
        return { kind: "already_answered" };
      }
      const u = unavailableForError(e.code);
      const retry = e.code === "network" ? ("load" as const) : undefined;
      return { kind: "unavailable", title: u.title, message: u.message, tone: u.tone, retry };
    }
    const u = unavailableForError("unknown");
    return { kind: "unavailable", title: u.title, message: u.message, tone: u.tone };
  }
}

export function attachFormHandler(
  root: HTMLElement,
  ctx: {
    token: string;
    clientSubmissionId: string;
    consentVersion: string;
    getPrompt: () => StorybookPromptPayload;
    onViewChange: (view: PageView) => void;
    onRerender: (html: string) => void;
    onPendingSubmit?: (pending: PendingSubmit) => void;
    onSubmitSuccess?: () => void;
  }
): void {
  root.addEventListener("submit", async (ev) => {
    const form = (ev.target as HTMLElement).closest("#storybook-form");
    if (!form || !(form instanceof HTMLFormElement)) return;
    ev.preventDefault();

    const textarea = form.querySelector<HTMLTextAreaElement>("#response");
    const consent = form.querySelector<HTMLInputElement>("#consent");
    const errorEl = form.querySelector<HTMLParagraphElement>("#form-error");
    const submitBtn = form.querySelector<HTMLButtonElement>("#submit-btn");
    if (!textarea || !consent || !errorEl || !submitBtn) return;

    const text = textarea.value.trim();
    errorEl.hidden = true;
    errorEl.textContent = "";

    if (!text || text.length < MIN_RESPONSE_CHARS) {
      errorEl.textContent = LENGTH_VALIDATION_MESSAGE;
      errorEl.hidden = false;
      textarea.focus();
      return;
    }

    if (!consent.checked) {
      errorEl.textContent = "Please confirm you understand how your answer will be saved.";
      errorEl.hidden = false;
      consent.focus();
      return;
    }

    ctx.onPendingSubmit?.({
      text,
      consentVersion: ctx.consentVersion,
      clientSubmissionId: ctx.clientSubmissionId,
    });

    ctx.onViewChange({ kind: "submitting" });
    ctx.onRerender(render({ kind: "submitting" }));
    logSubmitStart();

    try {
      await submitExternalResponse(
        ctx.token,
        text,
        ctx.consentVersion,
        ctx.clientSubmissionId
      );
      logSubmitSuccess();
      ctx.onSubmitSuccess?.();
      ctx.onViewChange({
        kind: "success",
        message: "Your answer has been safely sent.",
      });
      ctx.onRerender(render({ kind: "success", message: "Your answer has been safely sent." }));
    } catch (e) {
      if (e instanceof StorybookApiError) {
        logSubmitFailure(e.code);
        if (e.code === "token_completed" || e.code === "response_exists") {
          ctx.onViewChange({ kind: "already_answered" });
          ctx.onRerender(render({ kind: "already_answered" }));
          return;
        }
        if (e.code === "content_required" || e.code === "consent_required") {
          const formView: PageView = {
            kind: "form",
            prompt: ctx.getPrompt(),
            consentVersion: ctx.consentVersion,
          };
          ctx.onViewChange(formView);
          ctx.onRerender(render(formView));
          queueMicrotask(() => {
            const err = root.querySelector<HTMLParagraphElement>("#form-error");
            const area = root.querySelector<HTMLTextAreaElement>("#response");
            const box = root.querySelector<HTMLInputElement>("#consent");
            if (err) {
              err.textContent =
                e.code === "consent_required"
                  ? "Please confirm you understand how your answer will be saved."
                  : "Please add a little more detail in your own words.";
              err.hidden = false;
            }
            if (area) area.value = text;
            if (box) box.checked = consent.checked;
          });
          return;
        }
        const u = unavailableForError(e.code);
        const retry = e.code === "network" ? ("submit" as const) : undefined;
        ctx.onViewChange({
          kind: "unavailable",
          title: u.title,
          message: u.message,
          tone: u.tone,
          retry,
        });
        ctx.onRerender(
          render({
            kind: "unavailable",
            title: u.title,
            message: u.message,
            tone: u.tone,
            retry,
          })
        );
        return;
      }
      logSubmitFailure("unknown");
      const u = unavailableForError("network");
      ctx.onViewChange({
        kind: "unavailable",
        title: u.title,
        message: u.message,
        tone: u.tone,
        retry: "submit",
      });
      ctx.onRerender(
        render({
          kind: "unavailable",
          title: u.title,
          message: u.message,
          tone: u.tone,
          retry: "submit",
        })
      );
    }
  });
}

export type PendingSubmit = {
  text: string;
  consentVersion: string;
  clientSubmissionId: string;
};

/** @deprecated Use attachFormHandler */
export const bindForm = attachFormHandler;
